import type { IUserSearchItem } from "@frontend-shared/types";
import { getUserHandle } from "@frontend-shared/utils/user";

// NOTE: The domain-agnostic half of the invite typeahead, extracted so a second
// dialog does not have to reimplement (or cross-import) it. The meeting dialog
// still re-exports these from its own utils, so nothing there had to change.

export const CMDK_ITEM_SELECTOR = '[cmdk-item=""]';
export const CMDK_ITEM_VALUE_ATTRIBUTE = "data-value";

export interface IGetNextHighlightedIndexProps {
  key: string;
  currentIndex: number;
  lastIndex: number;
}

// NOTE: -1 is a real state, not "nothing": it means the implicit first
// suggestion, which Enter selects and ArrowDown steps off. That is why the list
// wraps THROUGH -1 rather than clamping at either end.
export const getNextHighlightedIndexForKey = ({
  key,
  currentIndex,
  lastIndex,
}: IGetNextHighlightedIndexProps): number | null => {
  if (key === "ArrowDown") {
    return currentIndex >= lastIndex ? -1 : currentIndex + 1;
  }
  if (key === "ArrowUp") {
    return currentIndex <= -1 ? lastIndex : currentIndex - 1;
  }
  return null;
};

export interface IGetHighlightedSuggestionValueProps {
  suggestions: IUserSearchItem[];
  highlightedIndex: number;
}

// NOTE: NSAI-7736 — the highlighted row is named by the person's uuid, the cmdk
// value every suggestion row carries. It used to be the email, but a member the
// organization's IdP identifies without one has "" there: cmdk never highlights
// an empty value, and two such members would have shared it.
export const getHighlightedSuggestionValue = ({
  suggestions,
  highlightedIndex,
}: IGetHighlightedSuggestionValueProps): string => {
  if (highlightedIndex < 0 || highlightedIndex >= suggestions.length) {
    return "";
  }
  return suggestions[highlightedIndex].uuid;
};

export interface IGetUserSuggestionNameProps {
  user: IUserSearchItem;
  fallback: string;
}

// NOTE: NSAI-7736 — what an offered or picked person is called in a chip and in
// the row's accessible name: their name, else their email, else the IdP login
// name of a member with no email, else the dialog's "Unknown" wording — never an
// empty label.
export const getUserSuggestionName = ({
  user,
  fallback,
}: IGetUserSuggestionNameProps): string =>
  user.fullName?.trim() ||
  getUserHandle({ email: user.email, username: user.username }) ||
  fallback;

export interface IIsClickOutsideContainerProps {
  container: HTMLElement | null;
  target: Node;
}

export const isClickOutsideContainer = ({
  container,
  target,
}: IIsClickOutsideContainerProps): boolean =>
  container !== null && !container.contains(target);

export interface IScrollHighlightedSuggestionIntoViewProps {
  container: HTMLElement | null;
  highlightedValue: string;
}

// NOTE: Resolves the highlighted row by its cmdk `data-value` rather than by
// `data-selected` (NSAI-3173). When the highlight is driven by a controlled
// `value` prop — as the invite dropdown does — cmdk syncs it in a layout effect
// that writes its store directly and emits, so the items re-render with new
// `data-selected` in a LATER commit than the effect calling this helper. Reading
// `data-selected` therefore matched the PREVIOUSLY highlighted row, which is
// normally still on screen, so `block: "nearest"` no-oped and the real highlight
// walked off the edge of the list. `data-value` is stamped from each item's own
// layout effect and never depends on selection state, so it is already correct
// by the time this runs.
export const scrollHighlightedSuggestionIntoView = ({
  container,
  highlightedValue,
}: IScrollHighlightedSuggestionIntoViewProps): void => {
  if (!container) {
    return;
  }

  const suggestionItems = Array.from(
    container.querySelectorAll(CMDK_ITEM_SELECTOR),
  );

  // NOTE: cmdk trims the value it writes to the attribute, so trim here too or
  // a padded value would never match the row it belongs to.
  const targetValue = highlightedValue.trim();

  const targetItem = targetValue
    ? suggestionItems.find(
        (item) => item.getAttribute(CMDK_ITEM_VALUE_ATTRIBUTE) === targetValue,
      )
    : suggestionItems[0];

  targetItem?.scrollIntoView({ block: "nearest" });
};

export interface IShouldRemoveLastSelectedOnBackspaceProps {
  key: string;
  searchQuery: string;
  selectedCount: number;
}

export const shouldRemoveLastSelectedOnBackspace = ({
  key,
  searchQuery,
  selectedCount,
}: IShouldRemoveLastSelectedOnBackspaceProps): boolean =>
  key === "Backspace" && !searchQuery.trim() && selectedCount > 0;

export interface IShouldCloseDropdownOnEscapeProps {
  key: string;
  isDropdownVisible: boolean;
}

export const shouldCloseDropdownOnEscape = ({
  key,
  isDropdownVisible,
}: IShouldCloseDropdownOnEscapeProps): boolean =>
  key === "Escape" && isDropdownVisible;
